import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import BrandLogo from "@/components/BrandLogo";

export default function AdminLogin() {
  const { t, adminToken, setAdminAuth } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [needChange, setNeedChange] = useState(false);
  const [current, setCurrent] = useState("");
  const [np, setNp] = useState("");
  const [np2, setNp2] = useState("");

  React.useEffect(() => {
    if (adminToken) navigate("/admin", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      setAdminAuth(data.access_token, data.admin);
      if (data.force_password_change) {
        setNeedChange(true);
        setCurrent(password);
        toast.info(t("admin.force_change_title"));
      } else {
        toast.success("Welcome back");
        navigate("/admin");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.invalid"));
    } finally {
      setLoading(false);
    }
  };

  const submitChange = async (e) => {
    e.preventDefault();
    if (np.length < 10) return toast.error(t("admin.change_short"));
    if (np !== np2) return toast.error(t("admin.change_mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: np });
      toast.success(t("admin.change_success"));
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("quote.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-muted/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 shadow-xl">
        <BrandLogo />
        <h1 className="mt-6 font-serif-display text-3xl font-semibold text-secondary">
          {needChange ? t("admin.force_change_title") : t("admin.login_title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {needChange ? t("admin.force_change_subtitle") : t("admin.login_subtitle")}
        </p>

        {!needChange ? (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {t("admin.email")}
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="admin-email-input"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-xl border border-border/70 bg-background px-10 py-3 text-sm focus:border-primary focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                {t("admin.password")}
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="admin-password-input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-xl border border-border/70 bg-background px-10 py-3 text-sm focus:border-primary focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="admin-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? t("admin.signing_in") : t("admin.sign_in")}
            </button>
            <div className="text-center">
              <Link to="/admin/forgot-password" data-testid="admin-forgot-link" className="text-xs font-semibold text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submitChange} className="mt-8 space-y-4" data-testid="admin-change-password-form">
            <PwField label={t("admin.current")} testid="admin-current-password-input" value={current} onChange={(e) => setCurrent(e.target.value)} />
            <PwField label={t("admin.new_password")} testid="admin-new-password-input" value={np} onChange={(e) => setNp(e.target.value)} minLength={10} />
            <PwField label={t("admin.confirm_password")} testid="admin-confirm-password-input" value={np2} onChange={(e) => setNp2(e.target.value)} minLength={10} />
            <button
              type="submit"
              data-testid="admin-change-password-submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? t("admin.saving") : t("admin.save")}
            </button>
          </form>
        )}
      </div>
      <style>{`.tk-input-a { width:100%; border-radius:0.75rem; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.75rem 1rem; font-size:0.875rem; }
      .tk-input-a:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary) / 0.15); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function PwField({ label, testid, value, onChange, minLength }) {
  const [show, setShow] = useState(false);
  return (
    <Field label={label}>
      <div className="relative">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          required
          minLength={minLength}
          className="tk-input-a !pr-10"
        />
        <button
          type="button"
          data-testid={`${testid}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </Field>
  );
}
